import React, { useEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import RenderAnimation from "./components/RenderAnimation";
import AnimationControlPanel from "./components/animations/AnimationControlPanel";
import CircleRadiusControl from "./components/animations/CircleRadiusControl";
import GifDurationControl from "./components/GifDurationControl";
import ShapeSelector from "./components/ShapeSelector";
import { Icon } from "@iconify/react";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { FaFileExport, FaCompress, FaHeart, FaBars } from "react-icons/fa";
import MermaidRenderer from "./components/mermaid/MermaidRenderer";
import RemoveAnimation from "./components/animations/RemoveAnimation";
import ExportSvg from "./components/ExportSvg";
import AIAnimator from "./components/AIAnimator";
import templates from "./svgTemplates.json";
import SVGOptionsModal from "./components/SVGOptionsModal";
import GifExporter from "./components/GifExporter";
import { Accordion } from "react-bootstrap";
import { useTooltips } from "./hooks/useTooltips";
import useCanvasViewport from "./hooks/useCanvasViewport";
import useExportStatus from "./hooks/useExportStatus";
import { AnimationType } from "./types/animations";
import useRemoteSvgTemplates from "./hooks/useRemoteSvgTemplates";
import AnimationTimeline from "./components/animations/AnimationTimeline";
import { SvgTemplate } from "./types/templates";
import { ShapeKind } from "./types/shapes";

gsap.registerPlugin(MotionPathPlugin);

const localTemplateManifest: SvgTemplate[] = (templates as SvgTemplate[]).map((template) => ({
  ...template,
  source: "local",
}));

const App: React.FC = () => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const canvasStageRef = useRef<HTMLDivElement | null>(null);
  const svgContainerRef = useRef<HTMLDivElement | null>(null);
  const [circleColor, setCircleColor] = useState<string>("blue");
  const [circleRadius, setCircleRadius] = useState<number>(5);
  const [animationSpeed, setAnimationSpeed] = useState<number>(2);
  const [strokeColor, setStrokeColor] = useState<string>("#1e1e1e");
  const [shape, setShape] = useState<ShapeKind>("circle");
  const [selectedAnimation, setSelectedAnimation] = useState<AnimationType>(null);
  const [animationSequence, setAnimationSequence] = useState<AnimationType[]>([]);
  const [isSequencePlaying, setIsSequencePlaying] = useState(false);
  const [sequenceIndex, setSequenceIndex] = useState(0);
  const [gifDuration, setGifDuration] = useState<number>(10);
  const [uploadedImage, setUploadedImage] = useState<string | undefined>();
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const [imageWidth, _setImageWidth] = useState<number>(20);
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const [imageHeight, _setImageHeight] = useState<number>(20);
  const [showAI, setShowAI] = useState<boolean>(false);
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
  const [selectedElement, setSelectedElement] = useState<SVGElement | null>(null);
  const {
    zoomLevel,
    panPosition,
    isPanning,
    zoomIn: handleZoomIn,
    zoomOut: handleZoomOut,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useCanvasViewport(canvasStageRef);
  const { isExporting, exportProgress, startGifExport, startCompressedExport } = useExportStatus(svgContainerRef);
  const [mermaidSyntax, setMermaidSyntax] = useState<string>(''); // State to store Mermaid syntax
  const [showMermaid, setShowMermaid] = useState<boolean>(false); // State to control rendering
  const [sidebarActive, setSidebarActive] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isPanelVisible, setIsPanelVisible] = useState(!isMobile);
  const isMermaidActive = showMermaid && mermaidSyntax.trim().length > 0;

  const {
    templates: remoteTemplates,
    isLoading: isSyncingTemplates,
    error: remoteTemplatesError,
    refresh: refreshRemoteTemplates,
    fetchedAt: remoteTemplatesFetchedAt,
  } = useRemoteSvgTemplates();

  const templateLibrary = useMemo<SvgTemplate[]>(() => {
    const combined = [...localTemplateManifest, ...remoteTemplates];
    const seen = new Map<string, SvgTemplate>();

    combined.forEach((template) => {
      const key = template.path ?? template.name;
      if (!seen.has(key)) {
        seen.set(key, template);
      }
    });

    return Array.from(seen.values());
  }, [remoteTemplates]);

  useTooltips();

  useEffect(() => {
    // Listen for resize events
    const handleResize = () => {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
      setSidebarActive(!isMobileView);
      setIsPanelVisible(!isMobileView); // Hide panel on mobile by default
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    // Clean up listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sidebarItems = [
    {
      icon: "solar:cloud-upload-line-duotone",
      label: "Uploads",
      description: "Bring in templates and assets to build your scene.",
    },
    {
      icon: "solar:box-minimalistic-broken",
      label: "Shapes",
      description: "Customize geometry, colors, and custom artwork.",
    },
    {
      icon: "solar:clapperboard-line-duotone",
      label: "Animations",
      description: "Choose motion presets and tweak timing.",
    },
    {
      icon: "solar:videocamera-record-linear",
      label: "Export",
      description: "Fine-tune playback and download shareable files.",
    },
  ];

  const panelDescriptions = sidebarItems.reduce<Record<string, string>>((acc, item) => {
    acc[item.label] = item.description;
    return acc;
  }, {});

  // Handle image upload for animation
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleQueueAnimation = (animation: AnimationType) => {
    if (!animation) {
      return;
    }
    setAnimationSequence((previous) => [...previous, animation]);
  };

  const handleRemoveQueuedAnimation = (index: number) => {
    setAnimationSequence((previous) => previous.filter((_, position) => position !== index));
    setSequenceIndex((previousIndex) => {
      if (previousIndex > index) {
        return previousIndex - 1;
      }
      return previousIndex;
    });
  };

  const handleClearSequence = () => {
    setAnimationSequence([]);
    setSequenceIndex(0);
    setIsSequencePlaying(false);
  };

  const handlePlaySequence = () => {
    if (animationSequence.length === 0) {
      return;
    }
    setSequenceIndex(0);
    setIsSequencePlaying(true);
    setSelectedAnimation(animationSequence[0]);
  };

  const handleStopSequence = () => {
    setIsSequencePlaying(false);
  };

  const handleElementClick = (event: React.MouseEvent<SVGElement>) => {
    const clickedElement = event.target as SVGElement;
    setSelectedElement(clickedElement);
    console.log("slected element",clickedElement);
  };

   // Automatically updates the SVG container with uploaded content and attaches event listeners
  useEffect(() => {
    if (svgContent && svgContainerRef.current) {
      svgContainerRef.current.innerHTML = svgContent;

      // Add click handlers for selecting SVG elements once the SVG is injected
      const svgElement = svgContainerRef.current.querySelector("svg");
      if (svgElement) {
        const allSvgElements = svgElement.querySelectorAll(`
          circle, 
          rect, 
          path, 
          image, 
          line, 
          polygon, 
          polyline, 
          ellipse, 
          text, 
          g, 
          use, 
          foreignObject, 
          tspan, 
          symbol
        `);
        allSvgElements.forEach((element) => {
          element.addEventListener("click", handleElementClick as any);
        });

        return () => {
          allSvgElements.forEach((element) => {
            element.removeEventListener("click", handleElementClick as any);
          });
        };
      }
    }
  }, [svgContent]);

  useEffect(() => {
    if (!isSequencePlaying) {
      return;
    }

    if (animationSequence.length === 0) {
      setIsSequencePlaying(false);
      return;
    }

    if (sequenceIndex >= animationSequence.length) {
      setIsSequencePlaying(false);
      setSequenceIndex(0);
      return;
    }

    const activeAnimation = animationSequence[sequenceIndex];
    setSelectedAnimation(activeAnimation);

    const timeout = window.setTimeout(() => {
      setSequenceIndex((previousIndex) => previousIndex + 1);
    }, animationSpeed * 1000);

    return () => window.clearTimeout(timeout);
  }, [animationSequence, animationSpeed, isSequencePlaying, sequenceIndex]);

  useEffect(() => {
    if (animationSequence.length === 0) {
      setIsSequencePlaying(false);
      setSequenceIndex(0);
    }
  }, [animationSequence.length]);
  // Handle element click for Mermaid-rendered diagrams
  const handleMermaidElementClick = (element: SVGElement) => {
    setSelectedElement(element);
    console.log("selected mermaid",element);
  };

  const handleMermaidRender = () => {
    console.log('Mermaid diagram rendered');
  };
  const [uploadedImageForMermaid, setUploadedImageForMermaid] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState({ width: 50, height: 50 });

  // Handle image upload
  const handleImageUploadForMermaid = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedImageForMermaid(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleSidebar = () => {
    const nextState = !sidebarActive;
    setSidebarActive(nextState);
    setShowOverlay(nextState && isMobile);
  };

  const handleOverlayClick = () => {
    setSidebarActive(false);
    setShowOverlay(false);
  };

  const handleExportClick = () => {
    startGifExport(gifDuration * 1000);
  };

  const handleCompressedExportClick = () => {
    startCompressedExport(gifDuration * 1000);
  };

  const handleSidebarItemClick = (item: any) => {
    setExpandedPanel(expandedPanel === item.label ? null : item.label);
    if (isMobile) {
      setSidebarActive(false);
      setShowOverlay(false);
      setIsPanelVisible(true); // Show panel when selecting an item
    }
  };

  const togglePanel = () => {
    setIsPanelVisible(!isPanelVisible);
  };

  const shouldRenderPanel = isMobile || isPanelVisible;

  return (
    <div className={`app-shell ${isMobile ? "is-mobile" : ""}`}>
      {isMobile && (
        <div
          className={`nav-scrim ${showOverlay ? "active" : ""}`}
          onClick={handleOverlayClick}
        />
      )}

      <aside className={`nav-rail ${sidebarActive ? "active" : ""}`}>
        <div className="nav-brand">
          <img src="/logo.png" alt="Animate SVG logo" />
          <span>Animate SVG</span>
        </div>
        <nav className="nav-items">
          {sidebarItems.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`nav-pill ${expandedPanel === item.label ? "active" : ""}`}
              onClick={() => handleSidebarItemClick(item)}
              aria-pressed={expandedPanel === item.label}
            >
              <Icon icon={item.icon} width="24" height="24" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="workspace">
        <header className="workspace-header">
          <div className="header-left">
            {isMobile && (
              <button
                type="button"
                className="icon-button"
                onClick={toggleSidebar}
                aria-label="Toggle navigation"
              >
                {React.createElement(FaBars as any)}
              </button>
            )}
            <div className="header-titles">
              <h1>SVG Animator Studio</h1>
              <p>Design, animate, and export vector stories from a single canvas.</p>
            </div>
          </div>
          <div className="header-actions">
            <div className="zoom-chip" role="status" aria-live="polite">
              <button type="button" onClick={handleZoomOut} aria-label="Zoom out">
                –
              </button>
              <span>{Math.round(zoomLevel * 100)}%</span>
              <button type="button" onClick={handleZoomIn} aria-label="Zoom in">
                +
              </button>
            </div>
            <button
              type="button"
              className="header-cta"
              onClick={togglePanel}
              aria-expanded={isPanelVisible}
            >
              {isPanelVisible ? "Hide controls" : "Show controls"}
            </button>
          </div>
        </header>

        <div className="workspace-body">
          <section className="canvas-column">
            <div className="canvas-card">
              <div className="canvas-card__header">
                <h2>Canvas</h2>
                <p>Use the zoom controls or drag the surface to focus on precise details.</p>
              </div>
              <div
                ref={canvasStageRef}
                className={`canvas-stage ${isMermaidActive ? "canvas-stage--mermaid" : ""}`}
                style={
                  isMermaidActive
                    ? { cursor: "default" }
                    : {
                        transform: `scale(${zoomLevel}) translate(${panPosition.x}px, ${panPosition.y}px)`,
                        cursor: zoomLevel > 1 ? (isPanning ? "grabbing" : "grab") : "default",
                      }
                }
                onMouseDown={isMermaidActive ? undefined : handleMouseDown}
                onMouseMove={isMermaidActive ? undefined : handleMouseMove}
                onMouseUp={isMermaidActive ? undefined : handleMouseUp}
                onMouseLeave={isMermaidActive ? undefined : handleMouseUp}
              >
                <div ref={svgContainerRef} className="canvas-stage__diagram">
                  {showMermaid && (
                    <MermaidRenderer
                      mermaidSyntax={mermaidSyntax}
                      svgContainerRef={svgContainerRef}
                      onElementClick={handleMermaidElementClick}
                      onRender={handleMermaidRender}
                      selectedAnimation={selectedAnimation}
                      animationSpeed={animationSpeed}
                    />
                  )}
                </div>
                {selectedElement && (
                  <div className="selection-banner">
                    <span>Selected element: {selectedElement.tagName}</span>
                  </div>
                )}
              </div>
            </div>
            <RenderAnimation
              svgElement={svgContainerRef.current?.querySelector("svg") as SVGElement}
              selectedElement={selectedElement}
              selectedAnimation={selectedAnimation}
              circleColor={circleColor}
              circleRadius={circleRadius}
              animationSpeed={animationSpeed}
              strokeColor={strokeColor}
              shape={shape}
              uploadedImage={uploadedImage}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
            />
          </section>

          {shouldRenderPanel && (
            <aside className={`control-panel ${isPanelVisible ? "visible" : ""}`} aria-hidden={!isPanelVisible}>
            {expandedPanel ? (
              <div className="control-panel__content">
                <header className="control-panel__header">
                  <h2>{expandedPanel}</h2>
                  <p>{panelDescriptions[expandedPanel]}</p>
                </header>

                {expandedPanel === "Uploads" && (
                  <Accordion defaultActiveKey={["view", "templates"]} alwaysOpen>
                    <Accordion.Item eventKey="view">
                      <Accordion.Header>Zoom &amp; View</Accordion.Header>
                      <Accordion.Body>
                        <p className="helper-text">Pinch, scroll, or use the buttons below for pixel-perfect positioning.</p>
                        <div className="zoom-control">
                          <button className="zoom-btn" onClick={handleZoomOut} aria-label="Zoom out">
                            –
                          </button>
                          <span className="zoom-display">{Math.round(zoomLevel * 100)}%</span>
                          <button className="zoom-btn" onClick={handleZoomIn} aria-label="Zoom in">
                            +
                          </button>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="templates">
                      <Accordion.Header>Templates</Accordion.Header>
                      <Accordion.Body>
                        <p className="helper-text">Start from a curated layout or render Mermaid syntax instantly.</p>
                        <SVGOptionsModal
                          templates={templateLibrary}
                          setSvgContent={setSvgContent}
                          mermaidSyntax={mermaidSyntax}
                          setMermaidSyntax={setMermaidSyntax}
                          setShowMermaid={setShowMermaid}
                          templatesAreSyncing={isSyncingTemplates}
                          templatesError={remoteTemplatesError}
                          onRefreshTemplates={refreshRemoteTemplates}
                          templatesLastFetched={remoteTemplatesFetchedAt}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="assets">
                      <Accordion.Header>Uploads</Accordion.Header>
                      <Accordion.Body>
                        <p className="helper-text">Drop in supporting imagery for richer diagrams.</p>
                        <label htmlFor="mermaidImage" className="form-label">
                          Upload supplemental image
                        </label>
                        <input
                          id="mermaidImage"
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handleImageUploadForMermaid}
                        />
                        {uploadedImageForMermaid && (
                          <div className="asset-preview">
                            <img src={uploadedImageForMermaid} alt="Uploaded asset preview" />
                            <div className="form-grid">
                              <div>
                                <label htmlFor="assetWidth">Width</label>
                                <input
                                  id="assetWidth"
                                  type="number"
                                  className="form-control"
                                  value={imageSize.width}
                                  onChange={(e) =>
                                    setImageSize({ ...imageSize, width: parseInt(e.target.value, 10) || 0 })
                                  }
                                />
                              </div>
                              <div>
                                <label htmlFor="assetHeight">Height</label>
                                <input
                                  id="assetHeight"
                                  type="number"
                                  className="form-control"
                                  value={imageSize.height}
                                  onChange={(e) =>
                                    setImageSize({ ...imageSize, height: parseInt(e.target.value, 10) || 0 })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                )}

                {expandedPanel === "Shapes" && (
                  <Accordion defaultActiveKey={["palette", "geometry"]} alwaysOpen>
                    <Accordion.Item eventKey="palette">
                      <Accordion.Header>Palette</Accordion.Header>
                      <Accordion.Body>
                        <p className="helper-text">Set the brand hue once and reuse it across fills and strokes.</p>
                        <div className="form-stack">
                          <div className="form-group">
                            <label htmlFor="shapeColor">Fill</label>
                            <input
                              id="shapeColor"
                              type="color"
                              value={circleColor}
                              onChange={(e) => setCircleColor(e.target.value)}
                              className="form-control"
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="strokeColor">Stroke</label>
                            <input
                              id="strokeColor"
                              type="color"
                              value={strokeColor}
                              onChange={(e) => setStrokeColor(e.target.value)}
                              className="form-control"
                            />
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="geometry">
                      <Accordion.Header>Geometry</Accordion.Header>
                      <Accordion.Body>
                        <p className="helper-text">Pick a base shape and adjust its proportions to match your story.</p>
                        <ShapeSelector shape={shape} setShape={setShape} />
                        <CircleRadiusControl circleRadius={circleRadius} setCircleRadius={setCircleRadius} />
                      </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="custom">
                      <Accordion.Header>Custom Artwork</Accordion.Header>
                      <Accordion.Body>
                        <p className="helper-text">Upload bespoke artwork to replace the generated shapes.</p>
                        <label htmlFor="customShape" className="form-label">
                          Upload SVG or PNG
                        </label>
                        <input
                          id="customShape"
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                )}

                {expandedPanel === "Animations" && (
                  <Accordion defaultActiveKey={["presets"]} alwaysOpen>
                    <Accordion.Item eventKey="presets">
                      <Accordion.Header>Motion Presets</Accordion.Header>
                      <Accordion.Body>
                        <p className="helper-text">Hover any preset to preview and click to apply it to the selected element.</p>
                        <AnimationControlPanel
                          setSelectedAnimation={setSelectedAnimation}
                          animationSpeed={animationSpeed}
                          setAnimationSpeed={setAnimationSpeed}
                          onQueueAnimation={handleQueueAnimation}
                          queuedAnimations={animationSequence}
                        />
                        <AnimationTimeline
                          sequence={animationSequence}
                          onRemove={handleRemoveQueuedAnimation}
                          onClear={handleClearSequence}
                          onPlay={handlePlaySequence}
                          onStop={handleStopSequence}
                          isPlaying={isSequencePlaying}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="cleanup">
                      <Accordion.Header>Remove Animation</Accordion.Header>
                      <Accordion.Body>
                        <p className="helper-text">Reset elements to a static state if you need a clean slate.</p>
                        {svgContainerRef.current && svgContainerRef.current.querySelector("svg") && (
                          <RemoveAnimation
                            svgElement={svgContainerRef.current.querySelector("svg") as SVGElement}
                          />
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                )}

                {expandedPanel === "Export" && (
                  <div className="export-panel">
                    <div className="export-card">
                      <h3>Playback settings</h3>
                      <p className="helper-text">Adjust the duration to balance smoothness with file size.</p>
                      <GifDurationControl duration={gifDuration} setDuration={setGifDuration} />
                    </div>
                    <div className="export-card">
                      <h3>Export options</h3>
                      <p className="helper-text">Choose your preferred format. GIF exports include transparency.</p>
                      <div className="button-group">
                        <button type="button" className="styled-button" onClick={handleExportClick}>
                          {React.createElement(FaFileExport as any, { 'aria-hidden': 'true' })}
                          Export GIF
                        </button>
                        <button
                          type="button"
                          className="styled-button"
                          onClick={handleCompressedExportClick}
                        >
                          {React.createElement(FaCompress as any, { 'aria-hidden': 'true' })}
                          Export compressed GIF
                        </button>
                        <ExportSvg svgElement={svgContainerRef.current?.querySelector("svg")} />
                        <GifExporter svgContainerRef={svgContainerRef} />
                      </div>
                    </div>
                    <Accordion className="support-card">
                        <Accordion.Item eventKey="support">
                        <Accordion.Header>
                          {React.createElement(FaHeart as any, { 'aria-hidden': 'true' })}
                          Support the developer
                        </Accordion.Header>
                        <Accordion.Body>
                          <iframe
                            src="https://github.com/sponsors/error505/card"
                            title="Sponsor error505"
                            height="225"
                            width="100%"
                            style={{ border: "0" }}
                          ></iframe>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </div>
                )}
              </div>
            ) : (
              <div className="control-panel__empty">
                <h2>Select a tool</h2>
                <p>Pick a section from the navigation rail to reveal contextual controls.</p>
              </div>
            )}
            </aside>
          )}
        </div>
      </div>

      {isMobile && isPanelVisible && (
        <div
          className="panel-scrim"
          onClick={() => setIsPanelVisible(false)}
        />
      )}

      {isExporting && (
        <div className="loading-overlay" role="alert" aria-live="polite">
          <div className="loading-content">
            <p>Exporting GIF… {exportProgress}%</p>
            <div className="progress-bar" aria-hidden="true">
              <div className="progress-bar-fill" style={{ width: `${exportProgress}%` }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Floating AI Animator chat bubble */}
      <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 1200 }}>
        {!showAI && (
          <button
            aria-label="Open AI Animator"
            onClick={() => setShowAI(true)}
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: '#0d6efd',
              color: 'white',
              border: 'none',
              boxShadow: '0 6px 18px rgba(13,110,253,0.3)',
              fontSize: 22,
              cursor: 'pointer',
            }}
          >
            💬
          </button>
        )}

        {showAI && (
          <div style={{ width: 360, height: 560, background: 'white', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #eee' }}>
              <strong>AI Animator</strong>
              <div>
                <button onClick={() => setShowAI(false)} aria-label="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>
            </div>
            <div style={{ padding: 8, height: '100%', boxSizing: 'border-box', overflow: 'auto' }}>
              <AIAnimator svgContainerRef={svgContainerRef} setSelectedAnimation={setSelectedAnimation} setSelectedElement={setSelectedElement} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
